import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Layout from '../../components/Layout';
import { usePageCustomizer, CustomizeButton, CustomizerPanel, PageWidget } from '../../components/PageCustomizer';
import { exportCSV, csvDateStamp } from '../../utils/exportCSV';
import { useLicense } from '../../context/LicenseContext';
import { useToast } from '../../context/ToastContext';
import EmptyState from '../../components/EmptyState';

// ─── Widget config ────────────────────────────────────────────────────────────

const ASSETS_WIDGETS: PageWidget[] = [
  { id:'sites',       label:'Sites',           icon:'🏢', description:'Site hierarchy and location management', defaultVisible:true },
  { id:'equipment',   label:'Equipment',       icon:'🔧', description:'All equipment across sites with full details', defaultVisible:true },
  { id:'maintenance', label:'Maintenance',     icon:'🛠️', description:'Scheduled maintenance tasks and history', defaultVisible:true },
  { id:'maps',        label:'Floor Plans',     icon:'🗺️', description:'Per-site floor plans with equipment pins', defaultVisible:true },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface Site {
  id: string;
  name: string;
  address: string;
  type: string;
  contact: string;
  contactPhone: string;
  active: boolean;
  createdAt: string;
  notes: string;
}

interface Equipment {
  id: string;
  siteId: string;
  name: string;
  category: string;
  manufacturer: string;
  model: string;
  serial: string;
  location: string;
  status: 'operational' | 'maintenance' | 'faulty' | 'decommissioned' | 'end-of-life';
  installed: string;
  warrantyExpiry: string;
  purchaseCost: number;
  replacementCost: number;
  supplier: string;
  supplierContact: string;
  notes: string;
}

interface MaintenanceSchedule {
  id: string;
  equipmentId: string;
  siteId: string;
  title: string;
  description: string;
  frequency: 'weekly' | 'monthly' | 'quarterly' | 'biannual' | 'annual' | 'once';
  nextDue: string;
  lastDone: string | null;
  lastDoneBy: string | null;
  estimatedMinutes: number;
  estimatedCost: number;
}

interface MaintenanceLog {
  id: string;
  scheduleId: string;
  equipmentId: string;
  completedAt: string;
  completedBy: string;
  notes: string;
  actualCost: number;
}

interface MapPin {
  equipmentId: string;
  x: number; // percentage 0–100
  y: number;
}

interface SiteMap {
  siteId: string;
  templateId: string; // which floor plan template
  pins: MapPin[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const EQUIP_CATEGORIES = [
  'Camera','PTZ Camera','NVR/DVR','Access Controller','Card Reader',
  'Alarm Panel','Alarm Sensor','Duress Button','Intercom','PA System',
  'Barrier Gate','Turnstile','Lock/Maglock','Network Switch','UPS',
  'Guard Tour','CCTV Dome','Other',
];


const EQUIP_STATUSES: Equipment['status'][] = ['operational','maintenance','faulty','decommissioned','end-of-life'];
const STATUS_COLOR: Record<string, string> = {
  operational: '#22c55e', maintenance: '#f59e0b', faulty: '#ef4444',
  decommissioned: '#555555', 'end-of-life': '#94a3b8',
};
const STATUS_LABEL: Record<string, string> = {
  operational: 'Operational', maintenance: 'Maintenance', faulty: 'Faulty',
  decommissioned: 'Decommissioned', 'end-of-life': 'End of Life',
};

const FREQ_LABEL: Record<string, string> = {
  weekly:'Weekly', monthly:'Monthly', quarterly:'Quarterly',
  biannual:'Bi-Annual', annual:'Annual', once:'One-off',
};

const API_TOKEN = () => localStorage.getItem('lemai_token') || '';

// ─── Seed data ────────────────────────────────────────────────────────────────

const SEED_SITES: Site[] = [
  { id:'S001', name:'Town Hall',         address:'182 Church St, Parramatta NSW 2150',           type:'Council Building', contact:'K. Nguyen',    contactPhone:'02 9806 5000', active:true,  createdAt:'2024-01-01', notes:'Primary administration building. Security operations centre on-site.' },
  { id:'S002', name:'Civic Centre',      address:'Civic Place, Parramatta NSW 2150',              type:'Council Building', contact:'P. O\'Brien',  contactPhone:'02 9806 5100', active:true,  createdAt:'2024-01-01', notes:'Council chambers and administrative offices.' },
  { id:'S003', name:'Aquatic Centre',    address:'Murchison St, North Parramatta NSW 2151',       type:'Sports Facility',  contact:'D. Walsh',     contactPhone:'02 9806 5200', active:true,  createdAt:'2024-01-01', notes:'Olympic-class pool. High foot traffic zone.' },
  { id:'S004', name:'Council Depot',     address:'9 Garnet St, Pemulwuy NSW 2145',                type:'Depot',            contact:'R. Barker',    contactPhone:'02 9806 5300', active:true,  createdAt:'2024-01-01', notes:'Fleet and equipment storage. Controlled access required.' },
  { id:'S005', name:'Parramatta Square', address:'Argyle St, Parramatta NSW 2150',               type:'Council Building', contact:'A. Singh',     contactPhone:'02 9806 5400', active:true,  createdAt:'2024-01-01', notes:'New development precinct. Integrated security systems.' },
  { id:'S006', name:'River Walk',        address:'Parramatta River Foreshore, NSW 2150',         type:'Park/Recreation',  contact:'L. Tran',      contactPhone:'02 9806 5500', active:true,  createdAt:'2024-01-01', notes:'Public foreshore path. Lone worker zone.' },
];

const SEED_EQUIPMENT: Equipment[] = [
  { id:'A001', siteId:'S001', name:'Entry Camera North',            category:'Camera',           manufacturer:'Axis',        model:'P3245-V',              serial:'ACCC8E123456', location:'Main Entry',              status:'operational',  installed:'2022-03-15', warrantyExpiry:'2025-03-15', purchaseCost:1200,  replacementCost:1400,  supplier:'Axis Communications',  supplierContact:'1300 292 897', notes:'' },
  { id:'A002', siteId:'S002', name:'Server Room Door Controller',   category:'Access Controller', manufacturer:'ICT',         model:'Protege GX DIN',        serial:'ICT-GX-7789',  location:'B2 Server Room',          status:'operational',  installed:'2021-11-20', warrantyExpiry:'2024-11-20', purchaseCost:3500,  replacementCost:3800,  supplier:'ICT Group',            supplierContact:'1800 428 476', notes:'Warranty expired — renewal pending' },
  { id:'A003', siteId:'S004', name:'Perimeter PIR Detector',        category:'Alarm Sensor',     manufacturer:'Bosch',       model:'DS151i',                serial:'BSC-90234',    location:'East Fence Line',         status:'maintenance',  installed:'2020-06-10', warrantyExpiry:'2023-06-10', purchaseCost:280,   replacementCost:320,   supplier:'Bosch Security',       supplierContact:'13 2345',      notes:'Intermittent false alarms — sensor cleaning scheduled' },
  { id:'A004', siteId:'S001', name:'NVR Unit — Control Room',       category:'NVR/DVR',          manufacturer:'Milestone',   model:'M50',                   serial:'ML-NVR-4421',  location:'Control Room',            status:'operational',  installed:'2023-01-08', warrantyExpiry:'2026-01-08', purchaseCost:8900,  replacementCost:9500,  supplier:'Milestone Systems',    supplierContact:'02 8098 7600', notes:'' },
  { id:'A005', siteId:'S003', name:'PTZ Camera — Car Park',         category:'PTZ Camera',       manufacturer:'Hikvision',   model:'DS-2DE4425IWG',         serial:'HIK-PTZ-0032', location:'Car Park Level 1',        status:'faulty',       installed:'2021-04-22', warrantyExpiry:'2024-04-22', purchaseCost:2100,  replacementCost:2400,  supplier:'Hikvision',            supplierContact:'02 9460 0700', notes:'Pan motor failure — replacement ordered' },
  { id:'A006', siteId:'S001', name:'Duress Button Array',           category:'Duress Button',    manufacturer:'Ness',        model:'D8X',                   serial:'NES-D8-8812',  location:'Front Counter',           status:'end-of-life',  installed:'2018-09-01', warrantyExpiry:'2021-09-01', purchaseCost:450,   replacementCost:600,   supplier:'Ness Corporation',     supplierContact:'1300 787 862', notes:'Scheduled for replacement Q3 2026' },
  { id:'A007', siteId:'S001', name:'Intercom Station — Main Entry', category:'Intercom',         manufacturer:'Jacques',     model:'2Easy IP',              serial:'JAC-2E-0141',  location:'Main Entry Lobby',        status:'operational',  installed:'2023-05-12', warrantyExpiry:'2026-05-12', purchaseCost:1850,  replacementCost:2000,  supplier:'Jacques Technologies', supplierContact:'03 9561 5000', notes:'' },
  { id:'A008', siteId:'S001', name:'Alarm Panel — North Wing',      category:'Alarm Panel',      manufacturer:'ArmorIP',     model:'SIA DC-09',             serial:'ARP-NW-2200',  location:'North Wing Plant Room',   status:'operational',  installed:'2022-08-01', warrantyExpiry:'2025-08-01', purchaseCost:6200,  replacementCost:7000,  supplier:'ArmorIP',              supplierContact:'',             notes:'Live ArmorIP receiver — active' },
  { id:'A009', siteId:'S002', name:'Access Reader — Lift Lobby',    category:'Card Reader',      manufacturer:'ICT',         model:'Protege RDR-PKI',       serial:'ICT-RD-5512',  location:'Lift Lobby Level 1',      status:'operational',  installed:'2022-02-14', warrantyExpiry:'2025-02-14', purchaseCost:580,   replacementCost:650,   supplier:'ICT Group',            supplierContact:'1800 428 476', notes:'Warranty expired' },
  { id:'A010', siteId:'S002', name:'Fixed Camera — Council Chamber',category:'Camera',           manufacturer:'Axis',        model:'P3265-V',               serial:'ACCC8F334412', location:'Council Chamber',         status:'operational',  installed:'2023-09-01', warrantyExpiry:'2026-09-01', purchaseCost:1450,  replacementCost:1600,  supplier:'Axis Communications',  supplierContact:'1300 292 897', notes:'' },
  { id:'A011', siteId:'S005', name:'Barrier Gate — Car Park Entry', category:'Barrier Gate',     manufacturer:'CAME',        model:'GARD 4 Arm',            serial:'CME-BG-0078',  location:'Car Park Entry P1',       status:'operational',  installed:'2021-07-19', warrantyExpiry:'2024-07-19', purchaseCost:4200,  replacementCost:5000,  supplier:'Came Automotion',      supplierContact:'02 9560 4644', notes:'Warranty expired — service contract active' },
  { id:'A012', siteId:'S002', name:'Smoke Detector — Server Room',  category:'Alarm Sensor',     manufacturer:'Hochiki',     model:'Analogue Heat',         serial:'HOC-SD-9912',  location:'B2 Server Room',          status:'operational',  installed:'2020-11-10', warrantyExpiry:'2023-11-10', purchaseCost:120,   replacementCost:160,   supplier:'Hochiki',              supplierContact:'',             notes:'Annual test passed 2025-07' },
  { id:'A013', siteId:'S003', name:'CCTV Dome — Pool Concourse',    category:'CCTV Dome',        manufacturer:'Bosch',       model:'FLEXIDOME 5100i',       serial:'BSC-FD-7721',  location:'Pool Concourse',          status:'operational',  installed:'2023-03-08', warrantyExpiry:'2026-03-08', purchaseCost:1650,  replacementCost:1800,  supplier:'Bosch Security',       supplierContact:'13 2345',      notes:'' },
  { id:'A014', siteId:'S001', name:'Door Mag-Lock — Emergency Exit',category:'Lock/Maglock',     manufacturer:'Trimec',      model:'EM3600',                serial:'TRM-ML-0066',  location:'Emergency Exit Level 2',  status:'operational',  installed:'2022-04-18', warrantyExpiry:'2025-04-18', purchaseCost:380,   replacementCost:420,   supplier:'Trimec',               supplierContact:'',             notes:'Fire mode release tested and passing' },
  { id:'A015', siteId:'S001', name:'UPS Unit — Control Room',       category:'UPS',              manufacturer:'APC',         model:'Smart-UPS 3000VA',      serial:'APC-SU-1199',  location:'Control Room',            status:'operational',  installed:'2021-06-25', warrantyExpiry:'2024-06-25', purchaseCost:3200,  replacementCost:3800,  supplier:'APC by Schneider',     supplierContact:'1800 272 272', notes:'Battery replaced 2024-12' },
  { id:'A016', siteId:'S004', name:'Guard Tour Wand — Depot',       category:'Guard Tour',       manufacturer:'TourTag',     model:'TT-200',                serial:'TTG-200-0341', location:'Depot Security Hut',      status:'operational',  installed:'2023-10-01', warrantyExpiry:'2026-10-01', purchaseCost:420,   replacementCost:500,   supplier:'TourTag Systems',      supplierContact:'',             notes:'' },
  { id:'A017', siteId:'S006', name:'Speed Dome — River Promenade',  category:'PTZ Camera',       manufacturer:'Dahua',       model:'SD49425XB-HNR',         serial:'DAH-SD-6632',  location:'Promenade East End',      status:'operational',  installed:'2024-01-15', warrantyExpiry:'2027-01-15', purchaseCost:2800,  replacementCost:3000,  supplier:'Dahua Technology',     supplierContact:'1300 851 299', notes:'LPR analytics enabled' },
  { id:'A018', siteId:'S003', name:'Intercom Station — Aquatic',    category:'Intercom',         manufacturer:'Jacques',     model:'2Easy IP',              serial:'JAC-2E-0278',  location:'Reception Desk',          status:'maintenance',  installed:'2022-11-30', warrantyExpiry:'2025-11-30', purchaseCost:1850,  replacementCost:2000,  supplier:'Jacques Technologies', supplierContact:'03 9561 5000', notes:'SIP registration issue — under investigation' },
  { id:'A019', siteId:'S004', name:'DVR Unit — Depot CCTV',         category:'NVR/DVR',          manufacturer:'Dahua',       model:'DHI-XVR5116HS',         serial:'DAH-XVR-4432', location:'Security Office',         status:'operational',  installed:'2021-03-22', warrantyExpiry:'2024-03-22', purchaseCost:1100,  replacementCost:1400,  supplier:'Dahua Technology',     supplierContact:'1300 851 299', notes:'Warranty expired — replacement planned FY27' },
  { id:'A020', siteId:'S002', name:'Turnstile — Civic Centre Foyer',category:'Turnstile',        manufacturer:'Boon Edam',   model:'Trilock TC',            serial:'BOE-TL-0019',  location:'Main Foyer',              status:'operational',  installed:'2023-06-20', warrantyExpiry:'2026-06-20', purchaseCost:18500, replacementCost:21000, supplier:'Boon Edam',            supplierContact:'02 9456 6789', notes:'Annual maintenance completed on schedule' },
];

const SEED_SCHEDULES: MaintenanceSchedule[] = [
  { id:'M001', equipmentId:'A005', siteId:'S003', title:'PTZ Motor Inspection',       description:'Check pan/tilt motors, clean lens, test presets.', frequency:'quarterly', nextDue:'2026-04-30', lastDone:'2026-01-15', lastDoneBy:'Tech Services', estimatedMinutes:45, estimatedCost:120 },
  { id:'M002', equipmentId:'A001', siteId:'S001', title:'Camera Lens Clean & Check',   description:'Clean lens, check focus and field of view, verify recording.', frequency:'biannual', nextDue:'2026-06-15', lastDone:'2025-12-15', lastDoneBy:'J. Okafor',    estimatedMinutes:20, estimatedCost:0   },
  { id:'M003', equipmentId:'A004', siteId:'S001', title:'NVR Health Check',            description:'Verify storage, check disk health, confirm backup jobs running.', frequency:'monthly', nextDue:'2026-04-25', lastDone:'2026-03-25', lastDoneBy:'IT Security',  estimatedMinutes:30, estimatedCost:0   },
  { id:'M004', equipmentId:'A003', siteId:'S004', title:'PIR Sensor Clean & Calibrate',description:'Clean PIR lens, calibrate sensitivity, run walk test.', frequency:'quarterly', nextDue:'2026-05-10', lastDone:'2026-02-10', lastDoneBy:'Tech Services', estimatedMinutes:30, estimatedCost:80  },
  { id:'M005', equipmentId:'A015', siteId:'S001', title:'UPS Battery Test',            description:'Load test battery, check runtime, review event log.', frequency:'annual',    nextDue:'2026-06-25', lastDone:'2025-06-25', lastDoneBy:'APC Technician',estimatedMinutes:60, estimatedCost:350 },
  { id:'M006', equipmentId:'A012', siteId:'S002', title:'Smoke Detector Annual Test',  description:'Function test smoke detector, clean sensing chamber, log result.', frequency:'annual', nextDue:'2026-07-22', lastDone:'2025-07-22', lastDoneBy:'Fire Services', estimatedMinutes:20, estimatedCost:0  },
  { id:'M007', equipmentId:'A011', siteId:'S005', title:'Barrier Gate Service',        description:'Lubricate arm mechanism, check limit switches, test safety loops.', frequency:'biannual', nextDue:'2026-05-05', lastDone:'2025-11-05', lastDoneBy:'CAME Tech',     estimatedMinutes:90, estimatedCost:280 },
  { id:'M008', equipmentId:'A006', siteId:'S001', title:'Duress Button Function Test', description:'Test each duress button, confirm signal reaches SOC, log results.', frequency:'monthly', nextDue:'2026-04-28', lastDone:'2026-03-28', lastDoneBy:'J. Okafor',    estimatedMinutes:15, estimatedCost:0  },
];

const SEED_LOGS: MaintenanceLog[] = [
  { id:'ML001', scheduleId:'M002', equipmentId:'A001', completedAt:'2025-12-15T09:30', completedBy:'J. Okafor',    notes:'Lens cleaned. Focus adjusted slightly. Recording verified on NVR.', actualCost:0   },
  { id:'ML002', scheduleId:'M001', equipmentId:'A005', completedAt:'2026-01-15T14:00', completedBy:'Tech Services', notes:'Pan motor lubricated. Tilt shows signs of wear — monitor closely.', actualCost:145 },
  { id:'ML003', scheduleId:'M003', equipmentId:'A004', completedAt:'2026-03-25T10:00', completedBy:'IT Security',  notes:'Storage at 61%. All disks healthy. Backup verified.', actualCost:0   },
  { id:'ML004', scheduleId:'M004', equipmentId:'A003', completedAt:'2026-02-10T11:00', completedBy:'Tech Services', notes:'Sensor cleaned. Sensitivity reduced by 15% to reduce false alarms. Walk test passed.', actualCost:80 },
  { id:'ML005', scheduleId:'M008', equipmentId:'A006', completedAt:'2026-03-28T09:00', completedBy:'J. Okafor',    notes:'All 4 duress buttons tested. Signals received in SOC within 3 seconds. Logged.', actualCost:0 },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function assetAge(installed: string): string {
  if (!installed) return '—';
  const diff = Date.now() - new Date(installed).getTime();
  const years = Math.floor(diff / (365.25 * 24 * 3600 * 1000));
  const months = Math.floor((diff % (365.25 * 24 * 3600 * 1000)) / (30.44 * 24 * 3600 * 1000));
  if (years > 0) return `${years}y ${months}m`;
  return `${months}m`;
}

function warrantyStatus(expiry: string): { label: string; color: string } {
  if (!expiry) return { label: 'Unknown', color: '#555555' };
  const days = (new Date(expiry).getTime() - Date.now()) / (24 * 3600 * 1000);
  if (days < 0) return { label: 'Expired', color: '#ef4444' };
  if (days < 90) return { label: `Expiring ${Math.ceil(days)}d`, color: '#f59e0b' };
  return { label: `Active`, color: '#22c55e' };
}

function daysUntil(dateStr: string | null): number {
  if (!dateStr) return 999;
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / (24 * 3600 * 1000));
}

function dueBadge(days: number): { label: string; color: string } {
  if (days < 0)  return { label: `Overdue ${Math.abs(days)}d`, color: '#ef4444' };
  if (days === 0) return { label: 'Due Today', color: '#f59e0b' };
  if (days <= 7)  return { label: `Due in ${days}d`, color: '#f59e0b' };
  if (days <= 30) return { label: `Due in ${days}d`, color: '#3b82f6' };
  return { label: `Due ${new Date(Date.now() + days * 86400000).toLocaleDateString('en-AU', { day:'numeric', month:'short' })}`, color: '#555555' };
}

function newId(prefix: string) {
  return `${prefix}${Date.now().toString(36).toUpperCase()}`;
}

const CARD = { background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:12, padding:'20px 24px' };
const BTN_PRIMARY = { background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:8, color:'white', padding:'8px 18px', fontSize:12, fontWeight:700, cursor:'pointer' as const };
const BTN_GHOST   = { background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'#a0a0a0', padding:'7px 16px', fontSize:12, fontWeight:600, cursor:'pointer' as const };
const INPUT_STYLE = { width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:7, padding:'8px 10px', color:'#e5e5e5', fontSize:12, outline:'none', boxSizing:'border-box' as const };
const SELECT_STYLE = { ...INPUT_STYLE, background:'#111111' };

// ─── Floor-plan templates (SVG paths describing room outlines) ───────────────

const FLOOR_TEMPLATES: Record<string, { label: string; rooms: { label: string; x: number; y: number; w: number; h: number; fill: string }[] }> = {
  'council-building': {
    label: 'Council Building',
    rooms: [
      { label:'Entry / Reception',    x:10, y:10, w:80, h:18, fill:'rgba(59,130,246,0.08)' },
      { label:'Main Office',          x:10, y:32, w:38, h:28, fill:'rgba(34,197,94,0.06)'  },
      { label:'Council Chamber',      x:52, y:32, w:38, h:28, fill:'rgba(168,85,247,0.08)' },
      { label:'Server Room',          x:10, y:64, w:20, h:18, fill:'rgba(239,68,68,0.08)'  },
      { label:'Lobby / Lifts',        x:34, y:64, w:22, h:18, fill:'rgba(100,116,139,0.1)' },
      { label:'Boardroom',            x:60, y:64, w:30, h:18, fill:'rgba(245,158,11,0.08)' },
      { label:'Car Park / Basement',  x:10, y:86, w:80, h:10, fill:'rgba(100,116,139,0.07)'},
    ],
  },
  'sports-facility': {
    label: 'Sports / Aquatic Facility',
    rooms: [
      { label:'Reception',       x:10, y:10, w:25, h:18, fill:'rgba(59,130,246,0.08)' },
      { label:'Change Rooms',    x:39, y:10, w:25, h:18, fill:'rgba(100,116,139,0.08)' },
      { label:'Plant Room',      x:68, y:10, w:22, h:18, fill:'rgba(239,68,68,0.08)'  },
      { label:'Pool Concourse',  x:10, y:32, w:80, h:36, fill:'rgba(34,197,94,0.05)'  },
      { label:'Gym / Studio',    x:10, y:72, w:38, h:18, fill:'rgba(168,85,247,0.07)' },
      { label:'Car Park',        x:52, y:72, w:38, h:18, fill:'rgba(100,116,139,0.07)' },
    ],
  },
  'depot': {
    label: 'Depot / Warehouse',
    rooms: [
      { label:'Security Hut / Entry', x:10, y:10, w:20, h:15, fill:'rgba(239,68,68,0.1)'   },
      { label:'Main Warehouse',       x:10, y:30, w:55, h:55, fill:'rgba(100,116,139,0.08)' },
      { label:'Workshop',             x:69, y:30, w:21, h:25, fill:'rgba(245,158,11,0.08)'  },
      { label:'Yard / Parking',       x:69, y:59, w:21, h:26, fill:'rgba(34,197,94,0.06)'   },
      { label:'Office Block',         x:10, y:88, w:35, h:10, fill:'rgba(59,130,246,0.08)'  },
      { label:'Hazmat Store',         x:49, y:88, w:41, h:10, fill:'rgba(239,68,68,0.07)'   },
    ],
  },
  'blank': {
    label: 'Custom / Blank',
    rooms: [
      { label:'Area A', x:10, y:10, w:38, h:38, fill:'rgba(59,130,246,0.06)' },
      { label:'Area B', x:52, y:10, w:38, h:38, fill:'rgba(34,197,94,0.06)'  },
      { label:'Area C', x:10, y:52, w:38, h:38, fill:'rgba(168,85,247,0.06)' },
      { label:'Area D', x:52, y:52, w:38, h:38, fill:'rgba(245,158,11,0.06)' },
    ],
  },
};

function siteTypeToTemplate(type: string): string {
  if (type === 'Sports Facility') return 'sports-facility';
  if (type === 'Depot') return 'depot';
  return 'council-building';
}

// ─── Sub-modals ───────────────────────────────────────────────────────────────

function SiteModal({ site, existingTypes, onSave, onClose }: { site: Partial<Site>; existingTypes: string[]; onSave: (s: Site) => void; onClose: () => void }) {
  const [f, setF] = useState<Partial<Site>>({ type: '', active: true, ...site });
  const set = (k: string, v: any) => setF(p => ({ ...p, [k]: v }));

  function save() {
    if (!f.name?.trim()) return;
    onSave({ id: f.id || newId('S'), name: f.name || '', address: f.address || '', type: f.type || '', contact: f.contact || '', contactPhone: f.contactPhone || '', active: f.active !== false, createdAt: f.createdAt || new Date().toISOString().slice(0, 10), notes: f.notes || '' });
    onClose();
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }}>
      <div style={{ background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:16, width:520, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ padding:'18px 24px 14px', borderBottom:'1px solid #1a1a1a', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h2 style={{ margin:0, fontSize:15, fontWeight:700, color:'#e5e5e5' }}>{site.id ? 'Edit Site' : 'Add New Site'}</h2>
          <button onClick={onClose} style={{ ...BTN_GHOST, padding:'4px 10px' }}>✕</button>
        </div>
        <div style={{ padding:'20px 24px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <div style={{ gridColumn:'span 2' }}>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Site Name *</label>
            <input style={INPUT_STYLE} value={f.name || ''} onChange={e => set('name', e.target.value)} placeholder="e.g. Town Hall" />
          </div>
          <div style={{ gridColumn:'span 2' }}>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Address</label>
            <input style={INPUT_STYLE} value={f.address || ''} onChange={e => set('address', e.target.value)} placeholder="Full street address" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Site Type</label>
            <input style={INPUT_STYLE} list="site-type-list" value={f.type || ''} onChange={e => set('type', e.target.value)} placeholder="e.g. Council Building, Childcare Centre, Toilet Block" />
            <datalist id="site-type-list">
              {existingTypes.map(t => <option key={t} value={t} />)}
            </datalist>
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Status</label>
            <select style={SELECT_STYLE} value={f.active ? 'active' : 'inactive'} onChange={e => set('active', e.target.value === 'active')}>
              <option value="active" style={{ background:'#111111' }}>Active</option>
              <option value="inactive" style={{ background:'#111111' }}>Inactive</option>
            </select>
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Site Contact</label>
            <input style={INPUT_STYLE} value={f.contact || ''} onChange={e => set('contact', e.target.value)} placeholder="Contact name" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Contact Phone</label>
            <input style={INPUT_STYLE} value={f.contactPhone || ''} onChange={e => set('contactPhone', e.target.value)} placeholder="02 9xxx xxxx" />
          </div>
          <div style={{ gridColumn:'span 2' }}>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Notes</label>
            <textarea style={{ ...INPUT_STYLE, resize:'none' as const }} rows={3} value={f.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Any site-specific notes..." />
          </div>
        </div>
        <div style={{ padding:'12px 24px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} style={BTN_GHOST}>Cancel</button>
          <button onClick={save} style={BTN_PRIMARY}>{site.id ? 'Save Changes' : 'Add Site'}</button>
        </div>
      </div>
    </div>
  );
}

function EquipmentModal({ equip, sites, onSave, onClose }: { equip: Partial<Equipment>; sites: Site[]; onSave: (e: Equipment) => void; onClose: () => void }) {
  const [f, setF] = useState<Partial<Equipment>>({ category: EQUIP_CATEGORIES[0], status: 'operational', ...equip });
  const set = (k: string, v: any) => setF(p => ({ ...p, [k]: v }));

  function save() {
    if (!f.name?.trim() || !f.siteId) return;
    onSave({ id: f.id || newId('EQ'), siteId: f.siteId || '', name: f.name || '', category: f.category || EQUIP_CATEGORIES[0], manufacturer: f.manufacturer || '', model: f.model || '', serial: f.serial || '', location: f.location || '', status: (f.status as Equipment['status']) || 'operational', installed: f.installed || '', warrantyExpiry: f.warrantyExpiry || '', purchaseCost: Number(f.purchaseCost) || 0, replacementCost: Number(f.replacementCost) || 0, supplier: f.supplier || '', supplierContact: f.supplierContact || '', notes: f.notes || '' });
    onClose();
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }}>
      <div style={{ background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:16, width:640, maxWidth:'95vw', maxHeight:'92vh', overflowY:'auto' }}>
        <div style={{ padding:'18px 24px 14px', borderBottom:'1px solid #1a1a1a', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h2 style={{ margin:0, fontSize:15, fontWeight:700, color:'#e5e5e5' }}>{equip.id ? 'Edit Equipment' : 'Add Equipment'}</h2>
            <p style={{ margin:'3px 0 0', fontSize:11, color:'#555555' }}>All fields help calculate lifecycle cost and schedule maintenance accurately</p>
          </div>
          <button onClick={onClose} style={{ ...BTN_GHOST, padding:'4px 10px' }}>✕</button>
        </div>
        <div style={{ padding:'20px 24px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <div style={{ gridColumn:'span 2' }}>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Equipment Name *</label>
            <input style={INPUT_STYLE} value={f.name || ''} onChange={e => set('name', e.target.value)} placeholder="Descriptive name, e.g. Entry Camera North" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Site *</label>
            <select style={SELECT_STYLE} value={f.siteId || ''} onChange={e => set('siteId', e.target.value)}>
              <option value="" style={{ background:'#111111' }}>— Select Site —</option>
              {sites.map(s => <option key={s.id} value={s.id} style={{ background:'#111111' }}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Category</label>
            <select style={SELECT_STYLE} value={f.category || EQUIP_CATEGORIES[0]} onChange={e => set('category', e.target.value)}>
              {EQUIP_CATEGORIES.map(c => <option key={c} style={{ background:'#111111' }}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Manufacturer</label>
            <input style={INPUT_STYLE} value={f.manufacturer || ''} onChange={e => set('manufacturer', e.target.value)} placeholder="e.g. Axis, Hikvision" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Model</label>
            <input style={INPUT_STYLE} value={f.model || ''} onChange={e => set('model', e.target.value)} placeholder="Model number / name" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Serial Number</label>
            <input style={INPUT_STYLE} value={f.serial || ''} onChange={e => set('serial', e.target.value)} placeholder="Serial / asset tag" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Location on Site</label>
            <input style={INPUT_STYLE} value={f.location || ''} onChange={e => set('location', e.target.value)} placeholder="e.g. Main Entry, Level 2, Car Park" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Status</label>
            <select style={SELECT_STYLE} value={f.status || 'operational'} onChange={e => set('status', e.target.value as Equipment['status'])}>
              {EQUIP_STATUSES.map(s => <option key={s} value={s} style={{ background:'#111111' }}>{STATUS_LABEL[s]}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Install Date</label>
            <input style={INPUT_STYLE} type="date" value={f.installed || ''} onChange={e => set('installed', e.target.value)} />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Warranty Expiry</label>
            <input style={INPUT_STYLE} type="date" value={f.warrantyExpiry || ''} onChange={e => set('warrantyExpiry', e.target.value)} />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Purchase Cost ($)</label>
            <input style={INPUT_STYLE} type="number" value={f.purchaseCost || ''} onChange={e => set('purchaseCost', e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Replacement Cost ($)</label>
            <input style={INPUT_STYLE} type="number" value={f.replacementCost || ''} onChange={e => set('replacementCost', e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Supplier</label>
            <input style={INPUT_STYLE} value={f.supplier || ''} onChange={e => set('supplier', e.target.value)} placeholder="Supplier company name" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Supplier Contact</label>
            <input style={INPUT_STYLE} value={f.supplierContact || ''} onChange={e => set('supplierContact', e.target.value)} placeholder="Phone or email" />
          </div>
          <div style={{ gridColumn:'span 2' }}>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Notes</label>
            <textarea style={{ ...INPUT_STYLE, resize:'none' as const }} rows={3} value={f.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Any relevant notes — faults, service history highlights, etc." />
          </div>
        </div>
        <div style={{ padding:'12px 24px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} style={BTN_GHOST}>Cancel</button>
          <button onClick={save} style={BTN_PRIMARY}>{equip.id ? 'Save Changes' : 'Add Equipment'}</button>
        </div>
      </div>
    </div>
  );
}

function MaintenanceModal({ schedule, sites, equipment, onSave, onClose }: { schedule: Partial<MaintenanceSchedule>; sites: Site[]; equipment: Equipment[]; onSave: (s: MaintenanceSchedule) => void; onClose: () => void }) {
  const [f, setF] = useState<Partial<MaintenanceSchedule>>({ frequency: 'monthly', estimatedMinutes: 30, estimatedCost: 0, ...schedule });
  const set = (k: string, v: any) => setF(p => ({ ...p, [k]: v }));

  const siteEquipment = useMemo(() => f.siteId ? equipment.filter(e => e.siteId === f.siteId) : equipment, [f.siteId, equipment]);

  function save() {
    if (!f.title?.trim() || !f.equipmentId || !f.nextDue) return;
    const eq = equipment.find(e => e.id === f.equipmentId);
    onSave({ id: f.id || newId('MS'), equipmentId: f.equipmentId || '', siteId: eq?.siteId || f.siteId || '', title: f.title || '', description: f.description || '', frequency: (f.frequency as MaintenanceSchedule['frequency']) || 'monthly', nextDue: f.nextDue || '', lastDone: f.lastDone || null, lastDoneBy: f.lastDoneBy || null, estimatedMinutes: Number(f.estimatedMinutes) || 0, estimatedCost: Number(f.estimatedCost) || 0 });
    onClose();
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }}>
      <div style={{ background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:16, width:540, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ padding:'18px 24px 14px', borderBottom:'1px solid #1a1a1a', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h2 style={{ margin:0, fontSize:15, fontWeight:700, color:'#e5e5e5' }}>{schedule.id ? 'Edit Schedule' : 'Add Maintenance Schedule'}</h2>
          <button onClick={onClose} style={{ ...BTN_GHOST, padding:'4px 10px' }}>✕</button>
        </div>
        <div style={{ padding:'20px 24px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <div style={{ gridColumn:'span 2' }}>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Task Title *</label>
            <input style={INPUT_STYLE} value={f.title || ''} onChange={e => set('title', e.target.value)} placeholder="e.g. PTZ Motor Inspection" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Site</label>
            <select style={SELECT_STYLE} value={f.siteId || ''} onChange={e => { set('siteId', e.target.value); set('equipmentId', ''); }}>
              <option value="" style={{ background:'#111111' }}>— All Sites —</option>
              {sites.map(s => <option key={s.id} value={s.id} style={{ background:'#111111' }}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Equipment *</label>
            <select style={SELECT_STYLE} value={f.equipmentId || ''} onChange={e => set('equipmentId', e.target.value)}>
              <option value="" style={{ background:'#111111' }}>— Select Equipment —</option>
              {siteEquipment.map(e => <option key={e.id} value={e.id} style={{ background:'#111111' }}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Frequency</label>
            <select style={SELECT_STYLE} value={f.frequency || 'monthly'} onChange={e => set('frequency', e.target.value)}>
              {Object.entries(FREQ_LABEL).map(([k, v]) => <option key={k} value={k} style={{ background:'#111111' }}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Next Due Date *</label>
            <input style={INPUT_STYLE} type="date" value={f.nextDue || ''} onChange={e => set('nextDue', e.target.value)} />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Est. Time (minutes)</label>
            <input style={INPUT_STYLE} type="number" value={f.estimatedMinutes || ''} onChange={e => set('estimatedMinutes', e.target.value)} placeholder="30" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Est. Cost ($)</label>
            <input style={INPUT_STYLE} type="number" value={f.estimatedCost || ''} onChange={e => set('estimatedCost', e.target.value)} placeholder="0" />
          </div>
          <div style={{ gridColumn:'span 2' }}>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Description</label>
            <textarea style={{ ...INPUT_STYLE, resize:'none' as const }} rows={3} value={f.description || ''} onChange={e => set('description', e.target.value)} placeholder="Steps or instructions for this maintenance task..." />
          </div>
        </div>
        <div style={{ padding:'12px 24px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} style={BTN_GHOST}>Cancel</button>
          <button onClick={save} style={BTN_PRIMARY}>{schedule.id ? 'Save Changes' : 'Add Schedule'}</button>
        </div>
      </div>
    </div>
  );
}

function CompleteTaskModal({ schedule, equipment, onComplete, onClose }: { schedule: MaintenanceSchedule; equipment: Equipment[]; onComplete: (log: MaintenanceLog) => void; onClose: () => void }) {
  const eq = equipment.find(e => e.id === schedule.equipmentId);
  const [notes, setNotes] = useState('');
  const [by, setBy] = useState('');
  const [cost, setCost] = useState(String(schedule.estimatedCost));

  function submit() {
    if (!by.trim()) return;
    onComplete({ id: newId('ML'), scheduleId: schedule.id, equipmentId: schedule.equipmentId, completedAt: new Date().toISOString().slice(0, 16), completedBy: by, notes, actualCost: Number(cost) || 0 });
    onClose();
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }}>
      <div style={{ background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:16, width:480, maxWidth:'95vw' }}>
        <div style={{ padding:'18px 24px 14px', borderBottom:'1px solid #1a1a1a' }}>
          <h2 style={{ margin:0, fontSize:15, fontWeight:700, color:'#22c55e' }}>✓ Mark Maintenance Complete</h2>
          <p style={{ margin:'4px 0 0', fontSize:12, color:'#555555' }}>{schedule.title} — {eq?.name || schedule.equipmentId}</p>
        </div>
        <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Completed By *</label>
            <input style={INPUT_STYLE} value={by} onChange={e => setBy(e.target.value)} placeholder="Your name or team" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Actual Cost ($)</label>
            <input style={INPUT_STYLE} type="number" value={cost} onChange={e => setCost(e.target.value)} placeholder="0" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, color:'#555555', marginBottom:4, fontWeight:500 }}>Notes / Observations</label>
            <textarea style={{ ...INPUT_STYLE, resize:'none' as const }} rows={4} value={notes} onChange={e => setNotes(e.target.value)} placeholder="What was done, what was found, any follow-up needed..." />
          </div>
        </div>
        <div style={{ padding:'12px 24px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} style={BTN_GHOST}>Cancel</button>
          <button onClick={submit} style={{ ...BTN_PRIMARY, background:'linear-gradient(135deg,#22c55e,#16a34a)' }}>Confirm Complete</button>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Sites ───────────────────────────────────────────────────────────────

function SitesTab({ sites, equipment, onAdd, onEdit, onDelete, onSelectSite }: { sites: Site[]; equipment: Equipment[]; onAdd: () => void; onEdit: (s: Site) => void; onDelete: (id: string) => void; onSelectSite: (id: string) => void }) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const equipCount = (siteId: string) => equipment.filter(e => e.siteId === siteId).length;
  const faultCount = (siteId: string) => equipment.filter(e => e.siteId === siteId && (e.status === 'faulty' || e.status === 'maintenance')).length;
  const totalCost  = (siteId: string) => equipment.filter(e => e.siteId === siteId).reduce((a, e) => a + e.purchaseCost, 0);

  const filtered = useMemo(() => {
    return sites.filter(s => {
      if (typeFilter && s.type !== typeFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return s.name.toLowerCase().includes(q) || s.address.toLowerCase().includes(q) || s.contact.toLowerCase().includes(q) || s.type.toLowerCase().includes(q);
      }
      return true;
    });
  }, [sites, search, typeFilter]);

  const siteTypes = useMemo(() => Array.from(new Set(sites.map(s => s.type))).sort(), [sites]);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <div>
          <h3 style={{ margin:0, fontSize:14, fontWeight:700, color:'#e5e5e5' }}>Site Hierarchy</h3>
          <p style={{ margin:'3px 0 0', fontSize:11, color:'#555555' }}>{sites.length} sites registered · {equipment.length} total equipment items</p>
        </div>
        <button style={BTN_PRIMARY} onClick={onAdd}>+ Add Site</button>
      </div>

      {/* Search & filter row */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <input
          style={{ ...INPUT_STYLE, width:260 }}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search sites by name, address, contact..."
        />
        <select style={{ ...SELECT_STYLE, width:180 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="" style={{ background:'#111111' }}>All Site Types</option>
          {siteTypes.map(t => <option key={t} value={t} style={{ background:'#111111' }}>{t}</option>)}
        </select>
        {(search || typeFilter) && (
          <button style={{ ...BTN_GHOST, fontSize:11 }} onClick={() => { setSearch(''); setTypeFilter(''); }}>Clear</button>
        )}
        {(search || typeFilter) && (
          <span style={{ fontSize:11, color:'#555555', alignSelf:'center' }}>{filtered.length} of {sites.length} shown</span>
        )}
      </div>

      {sites.length === 0 ? (
        <EmptyState icon="🏢" title="No sites yet" description="Add your first site to start building the asset and equipment registry." />
      ) : filtered.length === 0 ? (
        <EmptyState icon="🔍" title="No sites match" description={`No sites found for "${search || typeFilter}". Try a different search.`} />
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:14 }}>
          {filtered.map(site => {
            const ec = equipCount(site.id);
            const fc = faultCount(site.id);
            const tc = totalCost(site.id);
            return (
              <div key={site.id} style={{ ...CARD, cursor:'pointer', transition:'border-color 0.2s' }} onMouseEnter={e => (e.currentTarget.style.borderColor='rgba(59,130,246,0.4)')} onMouseLeave={e => (e.currentTarget.style.borderColor='rgba(255,255,255,0.07)')}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                  <div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                      <span style={{ fontSize:16 }}>🏢</span>
                      <span style={{ fontSize:14, fontWeight:700, color:'#e5e5e5' }}>{site.name}</span>
                      <span style={{ fontSize:10, padding:'2px 6px', borderRadius:4, background: site.active ? 'rgba(34,197,94,0.15)' : 'rgba(100,116,139,0.2)', color: site.active ? '#22c55e' : '#555555', fontWeight:600 }}>
                        {site.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div style={{ fontSize:11, color:'#555555' }}>{site.type}</div>
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    <button onClick={() => onEdit(site)} style={{ ...BTN_GHOST, padding:'4px 10px', fontSize:11 }}>Edit</button>
                    <button onClick={() => onDelete(site.id)} style={{ ...BTN_GHOST, padding:'4px 8px', fontSize:11, color:'#ef4444' }}>✕</button>
                  </div>
                </div>
                {site.address && <div style={{ fontSize:11, color:'#555555', marginBottom:10 }}>📍 {site.address}</div>}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
                  <div style={{ textAlign:'center', background:'rgba(59,130,246,0.08)', borderRadius:8, padding:'8px 4px' }}>
                    <div style={{ fontSize:18, fontWeight:800, color:'#3b82f6' }}>{ec}</div>
                    <div style={{ fontSize:10, color:'#555555' }}>Equipment</div>
                  </div>
                  <div style={{ textAlign:'center', background: fc > 0 ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)', borderRadius:8, padding:'8px 4px' }}>
                    <div style={{ fontSize:18, fontWeight:800, color: fc > 0 ? '#ef4444' : '#22c55e' }}>{fc}</div>
                    <div style={{ fontSize:10, color:'#555555' }}>Faults</div>
                  </div>
                  <div style={{ textAlign:'center', background:'rgba(168,85,247,0.08)', borderRadius:8, padding:'8px 4px' }}>
                    <div style={{ fontSize:14, fontWeight:800, color:'#a855f7' }}>${tc >= 1000 ? `${(tc/1000).toFixed(0)}k` : tc}</div>
                    <div style={{ fontSize:10, color:'#555555' }}>Asset Value</div>
                  </div>
                </div>
                {site.contact && <div style={{ fontSize:11, color:'#555555', marginBottom:8 }}>👤 {site.contact} {site.contactPhone && `· ${site.contactPhone}`}</div>}
                {site.notes && <div style={{ fontSize:11, color:'#555555', fontStyle:'italic', marginBottom:10 }}>{site.notes}</div>}
                <button onClick={() => onSelectSite(site.id)} style={{ width:'100%', ...BTN_GHOST, fontSize:11, textAlign:'center' as const }}>
                  View Equipment →
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Equipment ───────────────────────────────────────────────────────────

function EquipmentTab({ sites, equipment, onAdd, onEdit, onDelete, initSiteFilter }: { sites: Site[]; equipment: Equipment[]; onAdd: () => void; onEdit: (e: Equipment) => void; onDelete: (id: string) => void; initSiteFilter?: string }) {
  const [siteFilter, setSiteFilter] = useState(initSiteFilter || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return equipment.filter(e => {
      if (siteFilter && e.siteId !== siteFilter) return false;
      if (statusFilter && e.status !== statusFilter) return false;
      if (catFilter && e.category !== catFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return e.name.toLowerCase().includes(q) || e.model.toLowerCase().includes(q) || e.serial.toLowerCase().includes(q) || e.location.toLowerCase().includes(q);
      }
      return true;
    });
  }, [equipment, siteFilter, statusFilter, catFilter, search]);

  const totalValue = filtered.reduce((a, e) => a + e.purchaseCost, 0);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <div>
          <h3 style={{ margin:0, fontSize:14, fontWeight:700, color:'#e5e5e5' }}>Equipment Registry</h3>
          <p style={{ margin:'3px 0 0', fontSize:11, color:'#555555' }}>{filtered.length} items · Total value: ${totalValue.toLocaleString()}</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button style={{ ...BTN_GHOST, fontSize:11 }} onClick={() => { const hdrs = ['ID','Site','Name','Category','Manufacturer','Model','Serial','Location','Status','Installed','Warranty','Cost']; const rows = filtered.map(e => [e.id, sites.find(s=>s.id===e.siteId)?.name||e.siteId, e.name, e.category, e.manufacturer, e.model, e.serial, e.location, e.status, e.installed, e.warrantyExpiry, e.purchaseCost]); exportCSV(`assets-${csvDateStamp()}`, hdrs, rows); }}>Export CSV</button>
          <button style={BTN_PRIMARY} onClick={onAdd}>+ Add Equipment</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
        <input style={{ ...INPUT_STYLE, width:200 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, model, serial..." />
        <select style={{ ...SELECT_STYLE, width:160 }} value={siteFilter} onChange={e => setSiteFilter(e.target.value)}>
          <option value="" style={{ background:'#111111' }}>All Sites</option>
          {sites.map(s => <option key={s.id} value={s.id} style={{ background:'#111111' }}>{s.name}</option>)}
        </select>
        <select style={{ ...SELECT_STYLE, width:150 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="" style={{ background:'#111111' }}>All Statuses</option>
          {EQUIP_STATUSES.map(s => <option key={s} value={s} style={{ background:'#111111' }}>{STATUS_LABEL[s]}</option>)}
        </select>
        <select style={{ ...SELECT_STYLE, width:150 }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="" style={{ background:'#111111' }}>All Categories</option>
          {EQUIP_CATEGORIES.map(c => <option key={c} value={c} style={{ background:'#111111' }}>{c}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="🔧" title="No equipment found" description="Try adjusting filters or add equipment to get started." />
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
          {/* Header */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 140px 120px 100px 90px 90px 80px', gap:12, padding:'8px 14px', borderRadius:8, background:'#0d0d0d', fontSize:10, color:'#555555', fontWeight:700, textTransform:'uppercase' as const, letterSpacing:'0.05em' }}>
            <span>Equipment</span><span>Category</span><span>Location</span><span>Status</span><span>Age</span><span>Warranty</span><span style={{ textAlign:'right' as const }}>Cost</span>
          </div>
          {filtered.map(eq => {
            const site = sites.find(s => s.id === eq.siteId);
            const ws = warrantyStatus(eq.warrantyExpiry);
            const expanded = expandedId === eq.id;
            return (
              <div key={eq.id}>
                <div onClick={() => setExpandedId(expanded ? null : eq.id)} style={{ display:'grid', gridTemplateColumns:'1fr 140px 120px 100px 90px 90px 80px', gap:12, padding:'12px 14px', borderRadius:8, background: expanded ? 'rgba(59,130,246,0.06)' : 'rgba(255,255,255,0.02)', border:`1px solid ${expanded ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.05)'}`, cursor:'pointer', transition:'all 0.15s', alignItems:'center' }}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, color:'#e5e5e5' }}>{eq.name}</div>
                    <div style={{ fontSize:10, color:'#555555', marginTop:2 }}>{eq.id} · {site?.name || eq.siteId} · {eq.model}</div>
                  </div>
                  <span style={{ fontSize:11, color:'#a0a0a0' }}>{eq.category}</span>
                  <span style={{ fontSize:11, color:'#a0a0a0' }}>{eq.location}</span>
                  <span style={{ fontSize:11, padding:'2px 7px', borderRadius:4, background:`${STATUS_COLOR[eq.status]}20`, color:STATUS_COLOR[eq.status], fontWeight:600, whiteSpace:'nowrap' as const }}>{STATUS_LABEL[eq.status]}</span>
                  <span style={{ fontSize:11, color:'#555555' }}>{assetAge(eq.installed)}</span>
                  <span style={{ fontSize:10, color:ws.color, fontWeight:600 }}>{ws.label}</span>
                  <span style={{ fontSize:12, color:'#a0a0a0', fontFamily:'monospace', textAlign:'right' as const }}>${eq.purchaseCost.toLocaleString()}</span>
                </div>
                {expanded && (
                  <div style={{ margin:'2px 0 4px', padding:'16px 20px', background:'rgba(255,255,255,0.015)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'0 0 8px 8px', borderTop:'none' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:16, marginBottom:14 }}>
                      {[['Manufacturer', eq.manufacturer], ['Model', eq.model], ['Serial Number', eq.serial], ['Installed', eq.installed], ['Warranty Expiry', eq.warrantyExpiry], ['Supplier', eq.supplier], ['Supplier Contact', eq.supplierContact], ['Replacement Cost', eq.replacementCost ? `$${eq.replacementCost.toLocaleString()}` : '—']].map(([k, v]) => (
                        <div key={String(k)}>
                          <div style={{ fontSize:10, color:'#555555', fontWeight:600, textTransform:'uppercase' as const, letterSpacing:'0.04em', marginBottom:3 }}>{k}</div>
                          <div style={{ fontSize:12, color:'#cbd5e1' }}>{v || '—'}</div>
                        </div>
                      ))}
                    </div>
                    {eq.notes && <div style={{ fontSize:12, color:'#555555', fontStyle:'italic', marginBottom:14 }}>📝 {eq.notes}</div>}
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={e => { e.stopPropagation(); onEdit(eq); }} style={BTN_PRIMARY}>Edit</button>
                      <button onClick={e => { e.stopPropagation(); onDelete(eq.id); }} style={{ ...BTN_GHOST, color:'#ef4444' }}>Remove</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Maintenance ─────────────────────────────────────────────────────────

function MaintenanceTab({ sites, equipment, schedules, logs, onAdd, onEdit, onComplete, onDelete }: { sites: Site[]; equipment: Equipment[]; schedules: MaintenanceSchedule[]; logs: MaintenanceLog[]; onAdd: () => void; onEdit: (s: MaintenanceSchedule) => void; onComplete: (s: MaintenanceSchedule) => void; onDelete: (id: string) => void }) {
  const [siteFilter, setSiteFilter] = useState('');
  const [view, setView] = useState<'upcoming' | 'history'>('upcoming');

  const filtered = useMemo(() => {
    let s = schedules;
    if (siteFilter) s = s.filter(m => m.siteId === siteFilter);
    return s.sort((a, b) => daysUntil(a.nextDue) - daysUntil(b.nextDue));
  }, [schedules, siteFilter]);

  const filteredLogs = useMemo(() => {
    let l = logs;
    if (siteFilter) {
      const siteEqIds = new Set(equipment.filter(e => e.siteId === siteFilter).map(e => e.id));
      l = l.filter(log => siteEqIds.has(log.equipmentId));
    }
    return l.sort((a, b) => b.completedAt.localeCompare(a.completedAt));
  }, [logs, siteFilter, equipment]);

  const overdue   = filtered.filter(s => daysUntil(s.nextDue) < 0).length;
  const dueThisWeek = filtered.filter(s => { const d = daysUntil(s.nextDue); return d >= 0 && d <= 7; }).length;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <div>
          <h3 style={{ margin:0, fontSize:14, fontWeight:700, color:'#e5e5e5' }}>Maintenance Schedules</h3>
          <p style={{ margin:'3px 0 0', fontSize:11, color:'#555555' }}>
            {overdue > 0 && <span style={{ color:'#ef4444', fontWeight:700 }}>{overdue} overdue · </span>}
            {dueThisWeek > 0 && <span style={{ color:'#f59e0b', fontWeight:700 }}>{dueThisWeek} due this week · </span>}
            {filtered.length} total schedules
          </p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={onAdd} style={BTN_PRIMARY}>+ Add Schedule</button>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:16 }}>
        {[
          { label:'Total Schedules', val: schedules.length, color:'#3b82f6' },
          { label:'Overdue',         val: schedules.filter(s => daysUntil(s.nextDue) < 0).length,       color:'#ef4444' },
          { label:'Due This Week',   val: schedules.filter(s => { const d = daysUntil(s.nextDue); return d >= 0 && d <= 7; }).length, color:'#f59e0b' },
          { label:'Completed Tasks', val: logs.length, color:'#22c55e' },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ ...CARD, padding:'14px 18px', textAlign:'center' as const }}>
            <div style={{ fontSize:24, fontWeight:800, color }}>{val}</div>
            <div style={{ fontSize:11, color:'#555555', marginTop:4 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Tabs + Filter */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <div style={{ display:'flex', gap:2, background:'#0d0d0d', borderRadius:8, padding:3 }}>
          {(['upcoming', 'history'] as const).map(v => (
            <button key={v} onClick={() => setView(v)} style={{ padding:'6px 16px', borderRadius:6, border:'none', fontSize:12, fontWeight:600, cursor:'pointer', background: view===v ? 'rgba(59,130,246,0.2)' : 'transparent', color: view===v ? '#60a5fa' : '#555555', transition:'all 0.15s' }}>
              {v === 'upcoming' ? 'Upcoming' : 'History'}
            </button>
          ))}
        </div>
        <select style={{ ...SELECT_STYLE, width:160 }} value={siteFilter} onChange={e => setSiteFilter(e.target.value)}>
          <option value="" style={{ background:'#111111' }}>All Sites</option>
          {sites.map(s => <option key={s.id} value={s.id} style={{ background:'#111111' }}>{s.name}</option>)}
        </select>
      </div>

      {view === 'upcoming' && (
        filtered.length === 0 ? <EmptyState icon="🛠️" title="No schedules" description="Add a maintenance schedule to track upcoming tasks." /> : (
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {filtered.map(sch => {
              const eq = equipment.find(e => e.id === sch.equipmentId);
              const site = sites.find(s => s.id === sch.siteId);
              const days = daysUntil(sch.nextDue);
              const badge = dueBadge(days);
              return (
                <div key={sch.id} style={{ display:'grid', gridTemplateColumns:'1fr auto', alignItems:'center', gap:12, padding:'14px 16px', background:'#0d0d0d', border:'1px solid #1a1a1a', borderLeft:`3px solid ${badge.color}`, borderRadius:8 }}>
                  <div style={{ display:'grid', gridTemplateColumns:'200px 1fr 100px 90px 80px', gap:12, alignItems:'center' }}>
                    <div>
                      <div style={{ fontSize:12, fontWeight:700, color:'#e5e5e5' }}>{sch.title}</div>
                      <div style={{ fontSize:10, color:'#555555', marginTop:2 }}>{eq?.name || sch.equipmentId}</div>
                    </div>
                    <div style={{ fontSize:11, color:'#555555' }}>{site?.name || '—'} · {eq?.location || '—'}</div>
                    <div style={{ fontSize:10, color:'#555555', textAlign:'center' as const }}>
                      <div style={{ fontWeight:600, color:'#a0a0a0' }}>{FREQ_LABEL[sch.frequency]}</div>
                      <div>{sch.estimatedMinutes}m est.</div>
                    </div>
                    <div style={{ textAlign:'center' as const }}>
                      {sch.estimatedCost > 0 && <div style={{ fontSize:11, color:'#a0a0a0', fontWeight:600 }}>${sch.estimatedCost}</div>}
                    </div>
                    <span style={{ fontSize:10, padding:'3px 8px', borderRadius:5, background:`${badge.color}20`, color:badge.color, fontWeight:700, whiteSpace:'nowrap' as const, textAlign:'center' as const }}>{badge.label}</span>
                  </div>
                  <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                    <button onClick={() => onComplete(sch)} style={{ ...BTN_PRIMARY, padding:'6px 14px', fontSize:11, background:'linear-gradient(135deg,#22c55e,#16a34a)' }}>✓ Done</button>
                    <button onClick={() => onEdit(sch)} style={{ ...BTN_GHOST, padding:'5px 10px', fontSize:11 }}>Edit</button>
                    <button onClick={() => onDelete(sch.id)} style={{ ...BTN_GHOST, padding:'5px 8px', fontSize:11, color:'#ef4444' }}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {view === 'history' && (
        filteredLogs.length === 0 ? <EmptyState icon="📋" title="No history yet" description="Completed maintenance tasks will appear here." /> : (
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            {filteredLogs.map(log => {
              const eq = equipment.find(e => e.id === log.equipmentId);
              const sch = schedules.find(s => s.id === log.scheduleId);
              return (
                <div key={log.id} style={{ display:'grid', gridTemplateColumns:'1fr 140px 120px 80px', gap:12, padding:'12px 16px', background:'#0d0d0d', border:'1px solid rgba(255,255,255,0.05)', borderRadius:8, alignItems:'center' }}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, color:'#e5e5e5' }}>{sch?.title || 'Maintenance'}</div>
                    <div style={{ fontSize:10, color:'#555555', marginTop:2 }}>{eq?.name || log.equipmentId} {log.notes && `· ${log.notes.slice(0,60)}${log.notes.length > 60 ? '…' : ''}`}</div>
                  </div>
                  <div style={{ fontSize:11, color:'#555555' }}>{log.completedBy}</div>
                  <div style={{ fontSize:11, color:'#555555' }}>{log.completedAt.slice(0,10)}</div>
                  <div style={{ fontSize:12, color:'#22c55e', fontWeight:600, textAlign:'right' as const }}>
                    {log.actualCost > 0 ? `$${log.actualCost}` : <span style={{ color:'#22c55e' }}>✓ Done</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

// ─── Tab: Floor Plans ─────────────────────────────────────────────────────────

function FloorPlanTab({ sites, equipment, maps, onSaveMap }: { sites: Site[]; equipment: Equipment[]; maps: Record<string, SiteMap>; onSaveMap: (siteId: string, m: SiteMap) => void }) {
  const [selectedSite, setSelectedSite] = useState(sites[0]?.id || '');
  const [pins, setPins] = useState<MapPin[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [placing, setPlacing] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [mapMode, setMapMode] = useState<'view' | 'draw' | 'pins'>('view');
  const [drawMode, setDrawMode] = useState<'pencil' | 'eraser'>('pencil');
  const [drawColor, setDrawColor] = useState('#3b82f6');
  const [drawSize, setDrawSize] = useState(4);

  // Per-site floor images and drawings in localStorage
  const [floorImages, setFloorImages] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem('lemai_floor_images') || '{}'); } catch { return {}; }
  });
  const [floorDrawings, setFloorDrawings] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem('lemai_floor_drawings') || '{}'); } catch { return {}; }
  });

  const imgUploadRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingActive = useRef(false);
  const lastPt = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!selectedSite) return;
    const m = maps[selectedSite];
    const site = sites.find(s => s.id === selectedSite);
    setPins(m?.pins || []);
    setTemplateId(m?.templateId || (site ? siteTypeToTemplate(site.type) : 'council-building'));
    setSaved(false);
    setPlacing(null);
    setMapMode('view');
  }, [selectedSite, maps, sites]);

  // Load saved drawing onto canvas when site changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const savedDrawing = floorDrawings[selectedSite];
    if (savedDrawing) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = savedDrawing;
    }
  }, [selectedSite, floorDrawings]);

  const site = sites.find(s => s.id === selectedSite);
  const siteEquipment = equipment.filter(e => e.siteId === selectedSite);
  const template = FLOOR_TEMPLATES[templateId] || FLOOR_TEMPLATES['council-building'];
  const pinnedIds = new Set(pins.map(p => p.equipmentId));
  const floorImage = floorImages[selectedSite];

  function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const dataUrl = ev.target?.result as string;
      const next = { ...floorImages, [selectedSite]: dataUrl };
      setFloorImages(next);
      try { localStorage.setItem('lemai_floor_images', JSON.stringify(next)); } catch {}
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  function removeFloorImage() {
    const next = { ...floorImages };
    delete next[selectedSite];
    setFloorImages(next);
    try { localStorage.setItem('lemai_floor_images', JSON.stringify(next)); } catch {}
  }

  function getCanvasPt(e: React.MouseEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  }

  function persistDrawing() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const dataUrl = canvas.toDataURL('image/png');
      const next = { ...floorDrawings, [selectedSite]: dataUrl };
      setFloorDrawings(next);
      localStorage.setItem('lemai_floor_drawings', JSON.stringify(next));
    } catch {}
  }

  function clearDrawing() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    const next = { ...floorDrawings };
    delete next[selectedSite];
    setFloorDrawings(next);
    try { localStorage.setItem('lemai_floor_drawings', JSON.stringify(next)); } catch {}
  }

  function onCanvasDown(e: React.MouseEvent<HTMLCanvasElement>) {
    if (mapMode !== 'draw') return;
    drawingActive.current = true;
    const pt = getCanvasPt(e);
    lastPt.current = pt;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
    if (drawMode === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.arc(pt.x, pt.y, drawSize * 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
    } else {
      ctx.fillStyle = drawColor;
      ctx.arc(pt.x, pt.y, drawSize / 2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function onCanvasMove(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!drawingActive.current || mapMode !== 'draw' || !lastPt.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pt = getCanvasPt(e);
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (drawMode === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = drawSize * 4;
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.lineWidth = drawSize;
      ctx.strokeStyle = drawColor;
    }
    ctx.stroke();
    lastPt.current = pt;
  }

  function onCanvasUp() {
    if (drawingActive.current) {
      drawingActive.current = false;
      lastPt.current = null;
      if (mapMode === 'draw') persistDrawing();
    }
  }

  function handleMapClick(e: React.MouseEvent<SVGSVGElement>) {
    if (!placing || mapMode !== 'pins') return;
    const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPins(prev => [...prev.filter(p => p.equipmentId !== placing), { equipmentId: placing, x, y }]);
    setPlacing(null);
    setSaved(false);
  }

  function removePin(equipmentId: string) {
    setPins(prev => prev.filter(p => p.equipmentId !== equipmentId));
    setSaved(false);
  }

  function saveMap() {
    onSaveMap(selectedSite, { siteId: selectedSite, templateId, pins });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, flexWrap:'wrap', gap:8 }}>
        <div>
          <h3 style={{ margin:0, fontSize:14, fontWeight:700, color:'#e5e5e5' }}>Floor Plans</h3>
          <p style={{ margin:'3px 0 0', fontSize:11, color:'#555555' }}>Upload a JPEG/PNG floor plan or draw your own layout, then pin equipment locations.</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {!floorImage && (
            <select style={{ ...SELECT_STYLE, width:160 }} value={templateId} onChange={e => { setTemplateId(e.target.value); setSaved(false); }}>
              {Object.entries(FLOOR_TEMPLATES).map(([k, v]) => <option key={k} value={k} style={{ background:'#111111' }}>{v.label}</option>)}
            </select>
          )}
          <button onClick={saveMap} style={{ ...BTN_PRIMARY, background: saved ? 'linear-gradient(135deg,#22c55e,#16a34a)' : BTN_PRIMARY.background }}>
            {saved ? '✓ Saved!' : 'Save Layout'}
          </button>
        </div>
      </div>

      {/* Site selector */}
      <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
        {sites.map(s => (
          <button key={s.id} onClick={() => setSelectedSite(s.id)} style={{ padding:'6px 14px', borderRadius:8, border:`1px solid ${selectedSite===s.id?'rgba(59,130,246,0.6)':'rgba(255,255,255,0.1)'}`, background: selectedSite===s.id?'rgba(59,130,246,0.15)':'rgba(255,255,255,0.03)', color: selectedSite===s.id?'#60a5fa':'#555555', fontSize:12, fontWeight:600, cursor:'pointer', transition:'all 0.15s' }}>
            {s.name}
          </button>
        ))}
      </div>

      {sites.length === 0 ? <EmptyState icon="🏢" title="No sites yet" description="Add a site first, then you can upload or draw its floor plan." /> :
       !selectedSite ? <EmptyState icon="🗺️" title="No site selected" description="Select a site to view or edit its floor plan." /> : (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 260px', gap:16 }}>
          {/* Map area */}
          <div style={{ ...CARD, padding:0, overflow:'hidden' }}>
            {/* Toolbar */}
            <div style={{ padding:'10px 16px', borderBottom:'1px solid #1a1a1a', display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              <span style={{ fontSize:13, fontWeight:700, color:'#e5e5e5', marginRight:4 }}>{site?.name}</span>
              {/* Mode buttons */}
              {(['view','pins','draw'] as const).map(m => (
                <button key={m} onClick={() => { setMapMode(m); if (m !== 'pins') setPlacing(null); }}
                  style={{ padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:600, cursor:'pointer',
                    border:`1px solid ${mapMode===m?'rgba(59,130,246,0.5)':'rgba(255,255,255,0.08)'}`,
                    background: mapMode===m?'rgba(59,130,246,0.12)':'rgba(255,255,255,0.03)',
                    color: mapMode===m?'#60a5fa':'#555555', transition:'all 0.15s' }}>
                  {m==='view'?'👁 View':m==='pins'?'📍 Place Pins':'✏️ Draw'}
                </button>
              ))}
              <div style={{ flex:1 }} />
              {/* Image upload / remove */}
              <input ref={imgUploadRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display:'none' }} onChange={handleImageUpload} />
              <button onClick={() => imgUploadRef.current?.click()} style={{ padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:600, cursor:'pointer', background:'#111111', border:'1px solid rgba(255,255,255,0.1)', color:'#a0a0a0' }}>
                🖼 {floorImage ? 'Change image' : 'Upload image'}
              </button>
              {floorImage && (
                <button onClick={removeFloorImage} style={{ padding:'4px 8px', borderRadius:6, fontSize:11, cursor:'pointer', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', color:'#ef4444' }}>✕ Remove</button>
              )}
              {/* Draw tools — visible only in draw mode */}
              {mapMode === 'draw' && (
                <>
                  <button onClick={() => setDrawMode(drawMode === 'pencil' ? 'eraser' : 'pencil')}
                    style={{ padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:600, cursor:'pointer', background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.3)', color:'#f59e0b' }}>
                    {drawMode === 'pencil' ? '✏️ Pencil' : '⬜ Eraser'}
                  </button>
                  <input type="color" value={drawColor} onChange={e => setDrawColor(e.target.value)}
                    style={{ width:28, height:24, padding:2, borderRadius:4, border:'1px solid rgba(255,255,255,0.1)', background:'transparent', cursor:'pointer' }} title="Pen colour" />
                  <select value={drawSize} onChange={e => setDrawSize(Number(e.target.value))}
                    style={{ ...SELECT_STYLE, width:76, padding:'4px 6px', fontSize:11 }}>
                    {[2,4,6,10,16].map(s => <option key={s} value={s} style={{ background:'#111111' }}>{s}px</option>)}
                  </select>
                  <button onClick={clearDrawing} style={{ padding:'4px 8px', borderRadius:6, fontSize:11, cursor:'pointer', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', color:'#ef4444' }}>Clear</button>
                </>
              )}
              {mapMode === 'pins' && placing && (
                <span style={{ fontSize:11, color:'#f59e0b', fontWeight:600 }}>→ Click map: {equipment.find(e => e.id === placing)?.name}</span>
              )}
            </div>

            {/* Layered map canvas */}
            <div style={{ position:'relative', width:'100%', aspectRatio:'16/9', background:'rgba(0,0,0,0.3)', overflow:'hidden' }}>
              {/* Layer 1: background — uploaded image OR SVG template */}
              {floorImage ? (
                <img src={floorImage} alt="Floor plan" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'contain', pointerEvents:'none' }} />
              ) : (
                <svg viewBox="0 0 100 100" style={{ position:'absolute', inset:0, width:'100%', height:'100%' } as React.CSSProperties}>
                  {template.rooms.map(r => (
                    <g key={r.label}>
                      <rect x={r.x} y={r.y} width={r.w} height={r.h} fill={r.fill} stroke="rgba(255,255,255,0.08)" strokeWidth="0.3" rx="0.5" />
                      <text x={r.x + r.w/2} y={r.y + r.h/2 + 0.8} textAnchor="middle" fontSize="2.2" fill="rgba(255,255,255,0.25)" fontFamily="sans-serif">{r.label}</text>
                    </g>
                  ))}
                </svg>
              )}
              {/* Layer 2: drawing canvas */}
              <canvas
                ref={canvasRef}
                width={800} height={450}
                onMouseDown={onCanvasDown}
                onMouseMove={onCanvasMove}
                onMouseUp={onCanvasUp}
                onMouseLeave={onCanvasUp}
                style={{
                  position:'absolute', inset:0, width:'100%', height:'100%',
                  cursor: mapMode === 'draw' ? (drawMode === 'eraser' ? 'cell' : 'crosshair') : 'default',
                  pointerEvents: mapMode === 'draw' ? 'auto' : 'none',
                }}
              />
              {/* Layer 3: pin SVG (top — handles pin-placement clicks) */}
              <svg viewBox="0 0 100 100" onClick={handleMapClick}
                style={{
                  position:'absolute', inset:0, width:'100%', height:'100%',
                  cursor: mapMode === 'pins' && placing ? 'crosshair' : 'default',
                  pointerEvents: mapMode === 'pins' ? 'auto' : 'none',
                } as React.CSSProperties}
              >
                {pins.map(pin => {
                  const eq = equipment.find(e => e.id === pin.equipmentId);
                  const isPlacing = placing === pin.equipmentId;
                  return (
                    <g key={pin.equipmentId} style={{ cursor:'pointer' }}
                      onClick={ev => { ev.stopPropagation(); if (mapMode === 'pins') setPlacing(pin.equipmentId); }}>
                      <circle cx={pin.x} cy={pin.y} r="2.2" fill={isPlacing ? '#f59e0b' : '#3b82f6'} stroke="white" strokeWidth="0.4" />
                      <text x={pin.x} y={pin.y - 3} textAnchor="middle" fontSize="1.8" fill="white" fontFamily="sans-serif" fontWeight="bold">{eq?.name?.slice(0, 10) || pin.equipmentId}</text>
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>

          {/* Equipment panel */}
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <div style={{ fontSize:11, color:'#555555', fontWeight:700, textTransform:'uppercase' as const, letterSpacing:'0.06em', marginBottom:4 }}>
              Site Equipment ({siteEquipment.length})
            </div>
            {siteEquipment.length === 0 && (
              <div style={{ fontSize:12, color:'#555555' }}>No equipment registered for this site. Add equipment first.</div>
            )}
            {siteEquipment.map(eq => {
              const pinned = pinnedIds.has(eq.id);
              const isPlacing = placing === eq.id;
              return (
                <div key={eq.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', background: isPlacing ? 'rgba(245,158,11,0.1)' : pinned ? 'rgba(59,130,246,0.06)' : 'rgba(255,255,255,0.02)', border:`1px solid ${isPlacing ? 'rgba(245,158,11,0.4)' : pinned ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.06)'}`, borderRadius:8 }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background: pinned ? '#3b82f6' : '#222222', flexShrink:0 }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:11, fontWeight:600, color:'#e5e5e5', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' as const }}>{eq.name}</div>
                    <div style={{ fontSize:10, color:'#555555' }}>{eq.category}</div>
                  </div>
                  <div style={{ display:'flex', gap:4 }}>
                    <button onClick={() => { setMapMode('pins'); setPlacing(isPlacing ? null : eq.id); }}
                      style={{ ...BTN_GHOST, padding:'3px 8px', fontSize:10, color: isPlacing ? '#f59e0b' : pinned ? '#3b82f6' : '#555555' }}>
                      {isPlacing ? 'Cancel' : pinned ? 'Move' : 'Place'}
                    </button>
                    {pinned && <button onClick={() => removePin(eq.id)} style={{ ...BTN_GHOST, padding:'3px 6px', fontSize:10, color:'#ef4444' }}>✕</button>}
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop:8, padding:'10px 12px', background:'rgba(59,130,246,0.05)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:8, fontSize:11, color:'#555555' }}>
              {mapMode === 'draw'
                ? 'Drawing mode active. Use pencil to sketch layout, eraser to correct. Click Save Layout when done.'
                : mapMode === 'pins'
                  ? pins.length > 0 ? `${pins.length} pin${pins.length > 1?'s':''} placed. Click Save Layout to persist.` : 'Click "Place" next to any equipment, then click the map.'
                  : 'Upload a JPEG or use ✏️ Draw mode to sketch the floor plan. Use 📍 Place Pins to mark equipment.'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Assets() {
  const { isDemoOrg } = useLicense();
  const { toast } = useToast();
  const { isVisible, editing, setEditing, slots, toggle, moveUp, moveDown, reset: resetLayout } = usePageCustomizer('assets', ASSETS_WIDGETS);

  const [tab, setTab] = useState<'sites' | 'equipment' | 'maintenance' | 'maps'>('sites');
  const [equipSiteFilter, setEquipSiteFilter] = useState('');

  // Data state — seeded only for demo; live orgs start fresh from API
  const [sites, setSites] = useState<Site[]>(isDemoOrg ? SEED_SITES : []);
  const [equipment, setEquipment] = useState<Equipment[]>(isDemoOrg ? SEED_EQUIPMENT : []);
  const [schedules, setSchedules] = useState<MaintenanceSchedule[]>(isDemoOrg ? SEED_SCHEDULES : []);
  const [logs, setLogs] = useState<MaintenanceLog[]>(isDemoOrg ? SEED_LOGS : []);
  const [maps, setMaps] = useState<Record<string, SiteMap>>(() => {
    try { return JSON.parse(localStorage.getItem('lemai_asset_maps') || '{}'); } catch { return {}; }
  });

  // Modals
  const [siteModal, setSiteModal] = useState<{ open: boolean; site: Partial<Site> }>({ open: false, site: {} });
  const [equipModal, setEquipModal] = useState<{ open: boolean; equip: Partial<Equipment> }>({ open: false, equip: {} });
  const [scheduleModal, setScheduleModal] = useState<{ open: boolean; schedule: Partial<MaintenanceSchedule> }>({ open: false, schedule: {} });
  const [completeModal, setCompleteModal] = useState<MaintenanceSchedule | null>(null);

  // API helpers
  async function apiFetch(path: string, opts?: RequestInit) {
    if (isDemoOrg) return null;
    try {
      const r = await fetch(path, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${API_TOKEN()}`, ...(opts?.headers || {}) } });
      return r.ok ? r.json() : null;
    } catch { return null; }
  }

  // Load from API on mount (non-demo)
  useEffect(() => {
    if (isDemoOrg) return;
    Promise.all([
      apiFetch('/api/assets/sites').then(d => { if (d?.sites?.length) setSites(d.sites); }),
      apiFetch('/api/assets/equipment').then(d => { if (d?.equipment?.length) setEquipment(d.equipment); }),
      apiFetch('/api/assets/maintenance').then(d => { if (d?.schedules) setSchedules(d.schedules); if (d?.logs) setLogs(d.logs); }),
    ]);
  }, [isDemoOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  // Site actions
  async function saveSite(s: Site) {
    if (s.id && sites.find(x => x.id === s.id)) {
      setSites(prev => prev.map(x => x.id === s.id ? s : x));
      await apiFetch(`/api/assets/sites/${s.id}`, { method: 'PATCH', body: JSON.stringify(s) });
      toast(`Site "${s.name}" updated`, 'success');
    } else {
      setSites(prev => [...prev, s]);
      await apiFetch('/api/assets/sites', { method: 'POST', body: JSON.stringify(s) });
      toast(`Site "${s.name}" added`, 'success');
    }
  }

  async function deleteSite(id: string) {
    const s = sites.find(x => x.id === id);
    if (!window.confirm(`Remove site "${s?.name}"? This does not delete equipment.`)) return;
    setSites(prev => prev.filter(x => x.id !== id));
    await apiFetch(`/api/assets/sites/${id}`, { method: 'DELETE' });
    toast('Site removed', 'success');
  }

  // Equipment actions
  async function saveEquipment(eq: Equipment) {
    if (eq.id && equipment.find(x => x.id === eq.id)) {
      setEquipment(prev => prev.map(x => x.id === eq.id ? eq : x));
      await apiFetch(`/api/assets/equipment/${eq.id}`, { method: 'PATCH', body: JSON.stringify(eq) });
      toast(`"${eq.name}" updated`, 'success');
    } else {
      setEquipment(prev => [...prev, eq]);
      await apiFetch('/api/assets/equipment', { method: 'POST', body: JSON.stringify(eq) });
      toast(`"${eq.name}" added to registry`, 'success');
    }
  }

  async function deleteEquipment(id: string) {
    const eq = equipment.find(x => x.id === id);
    if (!window.confirm(`Remove "${eq?.name}" from registry?`)) return;
    setEquipment(prev => prev.filter(x => x.id !== id));
    await apiFetch(`/api/assets/equipment/${id}`, { method: 'DELETE' });
    toast('Equipment removed', 'success');
  }

  // Schedule actions
  async function saveSchedule(s: MaintenanceSchedule) {
    if (s.id && schedules.find(x => x.id === s.id)) {
      setSchedules(prev => prev.map(x => x.id === s.id ? s : x));
      await apiFetch(`/api/assets/maintenance/${s.id}`, { method: 'PATCH', body: JSON.stringify(s) });
      toast('Maintenance schedule updated', 'success');
    } else {
      setSchedules(prev => [...prev, s]);
      await apiFetch('/api/assets/maintenance', { method: 'POST', body: JSON.stringify(s) });
      toast('Maintenance schedule added', 'success');
    }
  }

  async function deleteSchedule(id: string) {
    if (!window.confirm('Remove this maintenance schedule?')) return;
    setSchedules(prev => prev.filter(x => x.id !== id));
    await apiFetch(`/api/assets/maintenance/${id}`, { method: 'DELETE' });
    toast('Schedule removed', 'success');
  }

  async function completeSchedule(log: MaintenanceLog, schedule: MaintenanceSchedule) {
    // Update last done on schedule
    const nextDueDays: Record<string, number> = { weekly:7, monthly:30, quarterly:91, biannual:183, annual:365, once:36500 };
    const days = nextDueDays[schedule.frequency] || 30;
    const nextDue = new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
    const updatedSch: MaintenanceSchedule = { ...schedule, lastDone: log.completedAt.slice(0, 10), lastDoneBy: log.completedBy, nextDue };
    setSchedules(prev => prev.map(s => s.id === schedule.id ? updatedSch : s));
    setLogs(prev => [log, ...prev]);
    await apiFetch(`/api/assets/maintenance/${schedule.id}/complete`, { method: 'POST', body: JSON.stringify(log) });
    toast(`"${schedule.title}" marked complete — next due ${nextDue}`, 'success');
  }

  // Map save
  function saveMap(siteId: string, m: SiteMap) {
    const updated = { ...maps, [siteId]: m };
    setMaps(updated);
    localStorage.setItem('lemai_asset_maps', JSON.stringify(updated));
    apiFetch(`/api/assets/maps/${siteId}`, { method: 'PUT', body: JSON.stringify(m) });
    toast('Floor plan saved', 'success');
  }

  const TAB = (key: typeof tab, label: string) => (
    <button onClick={() => setTab(key)} style={{ padding:'8px 18px', borderRadius:8, border:`1px solid ${tab===key?'rgba(59,130,246,0.5)':'transparent'}`, background: tab===key?'rgba(59,130,246,0.15)':'transparent', color: tab===key?'#60a5fa':'#555555', fontSize:13, fontWeight:600, cursor:'pointer', transition:'all 0.15s' }}>
      {label}
    </button>
  );

  // Summary stats for header
  const operational = equipment.filter(e => e.status === 'operational').length;
  const faults      = equipment.filter(e => e.status === 'faulty' || e.status === 'maintenance').length;
  const totalValue  = equipment.reduce((a, e) => a + e.purchaseCost, 0);
  const overdueCount = schedules.filter(s => daysUntil(s.nextDue) < 0).length;

  return (
    <Layout title="Asset Management">
      {editing && <CustomizerPanel widgets={ASSETS_WIDGETS} slots={slots} toggle={toggle} moveUp={moveUp} moveDown={moveDown} reset={resetLayout} onClose={() => setEditing(false)} />}
      <div style={{ padding:'24px 28px', maxWidth:1400 }}>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
          <div>
            <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:'#e5e5e5' }}>Asset Management</h1>
            <p style={{ margin:'5px 0 0', fontSize:13, color:'#555555' }}>
              {sites.length} sites · {equipment.length} equipment items · ${totalValue.toLocaleString()} total value
            </p>
          </div>
          <CustomizeButton editing={editing} setEditing={setEditing} />
        </div>

        {/* KPI strip */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10, marginBottom:24 }}>
          {[
            { label:'Total Sites',     val: sites.length,      color:'#3b82f6', icon:'🏢' },
            { label:'Equipment Items', val: equipment.length,  color:'#a855f7', icon:'🔧' },
            { label:'Operational',     val: operational,       color:'#22c55e', icon:'✓'  },
            { label:'Faults / Maint.', val: faults,            color: faults > 0 ? '#ef4444' : '#22c55e', icon:'⚠' },
            { label:'Overdue Tasks',   val: overdueCount,      color: overdueCount > 0 ? '#ef4444' : '#22c55e', icon:'🛠' },
          ].map(({ label, val, color, icon }) => (
            <div key={label} style={{ ...CARD, padding:'14px 18px', textAlign:'center' as const }}>
              <div style={{ fontSize:10, color:'#555555', marginBottom:6, fontWeight:600, textTransform:'uppercase' as const, letterSpacing:'0.05em' }}>{icon} {label}</div>
              <div style={{ fontSize:26, fontWeight:800, color, fontFamily:'monospace' }}>{val}</div>
            </div>
          ))}
        </div>

        {/* Tab bar */}
        <div style={{ display:'flex', gap:6, marginBottom:20, padding:'4px', background:'#0d0d0d', borderRadius:10, border:'1px solid #1a1a1a', width:'fit-content' }}>
          {TAB('sites',       '🏢 Sites')}
          {TAB('equipment',   '🔧 Equipment')}
          {TAB('maintenance', '🛠️ Maintenance')}
          {TAB('maps',        '🗺️ Floor Plans')}
        </div>

        {/* Tab content */}
        <div style={{ ...CARD }}>
          {tab === 'sites' && isVisible('sites') && (
            <SitesTab sites={sites} equipment={equipment}
              onAdd={() => setSiteModal({ open: true, site: {} })}
              onEdit={s => setSiteModal({ open: true, site: s })}
              onDelete={deleteSite}
              onSelectSite={id => { setEquipSiteFilter(id); setTab('equipment'); }}
            />
          )}
          {tab === 'equipment' && isVisible('equipment') && (
            <EquipmentTab sites={sites} equipment={equipment} initSiteFilter={equipSiteFilter}
              onAdd={() => setEquipModal({ open: true, equip: {} })}
              onEdit={eq => setEquipModal({ open: true, equip: eq })}
              onDelete={deleteEquipment}
            />
          )}
          {tab === 'maintenance' && isVisible('maintenance') && (
            <MaintenanceTab sites={sites} equipment={equipment} schedules={schedules} logs={logs}
              onAdd={() => setScheduleModal({ open: true, schedule: {} })}
              onEdit={s => setScheduleModal({ open: true, schedule: s })}
              onComplete={s => setCompleteModal(s)}
              onDelete={deleteSchedule}
            />
          )}
          {tab === 'maps' && isVisible('maps') && (
            <FloorPlanTab sites={sites} equipment={equipment} maps={maps} onSaveMap={saveMap} />
          )}
        </div>
      </div>

      {/* Modals */}
      {siteModal.open && (
        <SiteModal site={siteModal.site} existingTypes={Array.from(new Set(sites.map(s => s.type))).filter(Boolean).sort()} onSave={saveSite} onClose={() => setSiteModal({ open: false, site: {} })} />
      )}
      {equipModal.open && (
        <EquipmentModal equip={equipModal.equip} sites={sites} onSave={saveEquipment} onClose={() => setEquipModal({ open: false, equip: {} })} />
      )}
      {scheduleModal.open && (
        <MaintenanceModal schedule={scheduleModal.schedule} sites={sites} equipment={equipment} onSave={saveSchedule} onClose={() => setScheduleModal({ open: false, schedule: {} })} />
      )}
      {completeModal && (
        <CompleteTaskModal schedule={completeModal} equipment={equipment} onComplete={log => completeSchedule(log, completeModal)} onClose={() => setCompleteModal(null)} />
      )}
    </Layout>
  );
}
